/* eslint-disable class-methods-use-this */

'use strict';

/**
 * @typedef {Object} ExtensionCacheConfig
 * @property {string} id extension id
 * @property {string} version extension version
 * @property {string} baseUrl extension base URL
 * @property {Array<string>} resources extension resources to cache
 */

/**
 * @callback ExtensionCacheConfigGetter
 * @returns {ExtensionCacheConfig} extension cache configuration
 */

define('vbsw/private/serviceWorkerManagerClass',[
  'vbsw/private/utils',
  'vbsw/private/pwa/pwaUtils',
  'vbsw/private/emulatedServiceWorkerWrapper',
  'vbsw/private/emulatedXMLHttpRequest',
  'vbc/private/log',
], (Utils, PwaUtils, EmulatedServiceWorkerWrapper, createEmulatedXMLHttpRequest, Log) => {
  const logger = Log.getLogger('/vbsw/private/serviceWorkerManager');

  /**
   * Manages the installation and configuration of service workers, providing APIs for plugin installation
   * and offline handler management. This class handles both actual service worker registration and
   * emulation when service workers are not supported.
   */
  class ServiceWorkerManagerClass {
    constructor() {
      this.serviceWorkerWrapper = undefined;
      this.config = undefined;
    }

    /**
     * Installs the service worker and offline handler.
     *
     * @param {string} modulePath - The path from which the service worker loads its dependent modules.
     * @param {Object} externalConfig - Configuration object containing external require config, plugins, and logConfig.
     * @param {Object} Configuration - Object containing applicationUrl and getBaseUrlFromConfig().
     * @param {string} Configuration.applicationUrl - The application URL.
     * @param {Function} Configuration.getBaseUrlFromConfig - Function to get base URL from config.
     * @returns {Promise<EmulatedServiceWorkerWrapper[]>} Promise resolving to an array of EmulatedServiceWorkerWrapper
     *                                                    instances.
     * @example
     * serviceWorkerManager.install('/path/to/modules', externalConfig, {
     *   applicationUrl: 'https://example.com/app',
     *   getBaseUrlFromConfig: (location, vbConfig, applicationUrl) => { ... }
     * });
     */
    install(modulePath, externalConfig, Configuration) {
      return this.installServiceWorkers(modulePath, externalConfig, Configuration)
        .then((serviceWorkers) => this.installOfflineHandler()
          .then(() => serviceWorkers));
    }

    /**
     * Called by unit tests to clean up.
     *
     * @returns {Promise<void>}
     */
    uninstall() {
      const { serviceWorkerWrapper } = this;
      return (serviceWorkerWrapper ? serviceWorkerWrapper.uninstall() : Promise.resolve())
        .finally(() => {
          this.serviceWorkerWrapper = null;
          this.emulateServiceWorkerPromise = null;
          this.config = null;
        });
    }

    /**
     * Install service workers.
     * @param modulePath the path from which the service worker should its dependent modules
     * @param externalConfig config object containing external require config, plugins, and logConfig
     * @param Configuration applicationUrl and getBaseUrlFromConfig()
     * @returns {Promise<EmulatedServiceWorkerWrapper[]>}
     */
    installServiceWorkers(modulePath, externalConfig, Configuration) {
      return this.getServiceWorkerConfig(modulePath, externalConfig, Configuration)
        .then((config) => this.installServiceWorker(config.scopes[0], config))
        .then(() => [this.serviceWorkerWrapper]); // for backwards compatibility
    }

    /**
     * Activate the url mapper client running on the service worker.
     *
     * @returns {Promise}
     */
    activateUrlMapping() {
      return this.serviceWorkerWrapper ? this.serviceWorkerWrapper.activateUrlMapping() : Promise.resolve();
    }

    /**
     * Load and install the plugins for the given scope. The plugins should be an array of urls.
     *
     * @param plugins an array of urls for the plugins to install
     * @param reload if true, force reload all the plugins, otherwise, only reload plugins whose metadata has changed
     * @returns {Promise}
     */
    installServiceWorkerPlugins(plugins, reload = true) {
      return Promise.resolve().then(() => {
        // update the config.plugins so a newly found installing service worker can install them
        if (this.config) {
          this.config.plugins = plugins;
        }

        // protect against undefined serviceWorkerWrapper which can happen during unit tests
        return this.serviceWorkerWrapper ? this.serviceWorkerWrapper.installPlugins(plugins, reload) : undefined;
      });
    }

    /**
     * Install the given message handler with the registered service workers. A message handler can implement
     * a set of methods with parameters that can be invoked via messages posted from the service worker. Each method
     * should return a promise that resolves to a response or rejects with an error. Note that multiple message
     * handlers can be installed.
     *
     * A method on the message handler can be invoked from the service worker by calling Utils.postMessage with a
     * message shown below:
     *
     * {
     *   method: 'methodToCall',
     *   args: ['foo']
     * }
     *
     * Note that only the last installed message handler containing the method specified in the message will be
     * invoked. This means a message handler can override the behavior of a handler registered before it. If this
     * is not the intended behavior, make sure there is no naming conflicts.
     *
     * @param {Object} messageHandler - The message handler object containing methods that can be called
     *                                  from the service worker using Utils.postMessage.
     * @example
     * const messageHandler = {
     *   handleMessage: (message) => {
     *     // Handle the message
     *   }
     * };
     * serviceWorkerManager.installMessageHandler(messageHandler);
     */
    installMessageHandler(messageHandler) {
      if (this.serviceWorkerWrapper) {
        this.serviceWorkerWrapper.installMessageHandler(messageHandler);
      } else {
        // put the message handler on a queue so it can be installed when the service worker is installed
        this.messageHandlerQueue = this.messageHandlerQueue || [];
        this.messageHandlerQueue.push(messageHandler);
      }
    }

    /**
     * Uninstall the given message handler.
     *
     * @param messageHandler message handler to uninstall
     */
    uninstallMessageHandler(messageHandler) {
      if (this.serviceWorkerWrapper) {
        this.serviceWorkerWrapper.uninstallMessageHandler(messageHandler);
      }
    }

    /**
     * Load and install the offline handler.
     *
     * @returns {Promise}
     */
    installOfflineHandler() {
      return this.serviceWorkerWrapper.installOfflineHandler();
    }

    /**
     * Uninstall the offline handler.
     *
     * @returns {Promise}
     */
    uninstallOfflineHandler() {
      // make sure serviceWorkerWrapper exists to not break unit tests
      return this.serviceWorkerWrapper ? this.serviceWorkerWrapper.uninstallOfflineHandler() : Promise.resolve();
    }

    /**
     * Install a secondary offline handler defined in an app UI.
     *
     * @param handlerUrl the url from which to load the offline handler
     * @returns {Promise}
     */
    installSecondaryOfflineHandler(handlerUrl) {
      return this.serviceWorkerWrapper.installSecondaryOfflineHandler(handlerUrl);
    }

    /**
     * Uninstall the secondary offline handler.
     *
     * @returns {Promise}
     */
    uninstallSecondaryOfflineHandler() {
      // make sure serviceWorkerWrapper exists to not break unit tests
      return this.serviceWorkerWrapper ? this.serviceWorkerWrapper.uninstallSecondaryOfflineHandler()
        : Promise.resolve();
    }

    /**
     * Run any setup work that was deferred until after an offline-enabled application package has loaded.
     * @returns {Promise}
     */
    runPendingSetupActions() {
      return Promise.resolve();
    }

    /**
     * Invoke syncOfflineData on the service worker.
     *
     * @param options any options to be passed to the persistence sync manager
     * @returns {Promise}
     */
    syncOfflineData(options) {
      return this.serviceWorkerWrapper.syncOfflineData(options);
    }

    /**
     * Invoke getOfflineSyncLog on the service worker.
     *
     * @returns {Promise}
     */
    getOfflineSyncLog() {
      return this.serviceWorkerWrapper.getOfflineSyncLog();
    }

    /**
     * Invoke insertOfflineSyncRequest on the service worker.
     *
     * @param request
     * @param options
     * @returns {Promise}
     */
    insertOfflineSyncRequest(request, options) {
      return this.serviceWorkerWrapper.insertOfflineSyncRequest(request, options);
    }

    /**
     * Invoke removeOfflineSyncRequest on the service worker.
     *
     * @param requestId
     * @returns {Promise}
     */
    removeOfflineSyncRequest(requestId) {
      return this.serviceWorkerWrapper.removeOfflineSyncRequest(requestId);
    }

    /**
     * Invoke updateOfflineSyncRequest on the service worker.
     *
     * @param requestId
     * @param request
     * @returns {Promise}
     */
    updateOfflineSyncRequest(requestId, request) {
      return this.serviceWorkerWrapper.updateOfflineSyncRequest(requestId, request);
    }

    /**
     * If the flag is true, force the PersistenceManager offline and vice versa.
     *
     * @param flag if true, force the PersistenceManager offline and vice versa.
     * @returns {Promise}
     */
    forceOffline(flag) {
      return this.serviceWorkerWrapper.forceOffline(flag);
    }

    /**
     * Set options on the offline handler.
     *
     * @param options options to be set on the offline handler
     * @returns {Promise}
     */
    setOfflineHandlerOptions(options) {
      return this.serviceWorkerWrapper.setOfflineHandlerOptions(options);
    }

    /**
     * Set options on the secondary offline handler.
     *
     * @param options options to be set on the secondary offline handler
     * @returns {Promise}
     */
    setSecondaryOfflineHandlerOptions(options) {
      return this.serviceWorkerWrapper.setSecondaryOfflineHandlerOptions(options);
    }

    /**
     * Return a promise that resolves to true if the service worker is online, false otherwise.
     *
     * @return {Promise.<Boolean>}
     */
    isOnline() {
      return this.serviceWorkerWrapper.isOnline();
    }

    /**
     * Return the configuration object for the service worker. The configuration object should be specified on
     * the vbInitConfig as follow:
     *
     * SERVICE_WORKER_CONFIG: {
     *   scriptUrl: '/serviceWorker.js',
     *   scopes: ['/'],
     *   disabled: false
     * }
     *
     * The scriptUrl is the location where the service worker script is served. The scopes is an array of urls
     * that need to be relative to the scriptUrl. Otherwise, the service worker will fail to register. Both
     * scriptUrl and scope must be specified in order for them to take effect. Otherwise, a default scriptUrl
     * and scope will be derived from the CONTEXT_ROOT, APP_ID and APP_VERSION specified in vbInitConfig which
     * will be suitable for use in the VBCS environment.
     *
     * Setting the disabled flag to true will disable loading of the service worker and fall back to using
     * emulation.
     *
     * @param modulePath the path from which the service worker loads its dependent modules
     * @param externalConfig config object containing external require config, plugins, and logConfig
     * @param Configuration applicationUrl and getBaseUrlFromConfig()
     * @returns {Promise<Object>}
     * @private
     */
    getServiceWorkerConfig(modulePath, externalConfig, Configuration) {
      return Promise.resolve().then(() => {
        const vbConfig = window.vbInitConfig || {};
        const config = vbConfig.SERVICE_WORKER_CONFIG || {};

        config.applicationUrl = Configuration.applicationUrl;
        config.baseUrl = Configuration.getBaseUrlFromConfig(window.location, vbConfig, config.applicationUrl);

        // if scope is not provided, derive them from context root, app url prefix, app id and app version
        if (!config.scopes) {
          const appId = vbConfig.APP_ID;
          const appVersion = vbConfig.APP_VERSION;
          const appUrlPrefix = vbConfig.APP_URL_PREFIX;

          let contextRoot = vbConfig.CONTEXT_ROOT || '/';
          if (!contextRoot.startsWith('/')) {
            contextRoot = `/${contextRoot}`;
          }
          if (!contextRoot.endsWith('/')) {
            contextRoot = `${contextRoot}/`;
          }

          let scriptRoot;
          if (appId && appVersion) {
            // derive the script root for the VBCS environment
            scriptRoot = `${contextRoot}${appUrlPrefix}/${appId}/${appVersion}/`;
          } else {
            scriptRoot = contextRoot;
          }

          config.scopes = [scriptRoot];
        }

        config.log = vbConfig.LOG;

        // set up the JET path
        // NOTE: The service worker is dependent on the location of the JET to load requirejs. If this is not available,
        // the service worker will not be able to load any of the plugins.
        if (vbConfig.JET_CDN_PATH) {
          config.jetPath = `${vbConfig.JET_CDN_PATH}${vbConfig.JET_CDN_VERSION}/`;
        }

        config.baseUrlToken = vbConfig.BASE_URL_TOKEN;
        config.debug = vbConfig.DEBUG;

        // determine the url for the offline handler
        if (vbConfig.CONTEXT_OFFLINE_HANDLER_PATH) {
          // allow override of the default path processing
          config.offlineHandler = vbConfig.CONTEXT_OFFLINE_HANDLER_PATH;
        } else {
          // strip 'index.html' off window.location.pathname
          let path = window.location.pathname;

          const htmlName = 'index.html';
          const index = path.indexOf(htmlName);
          path = index >= 0 ? path.substring(0, index) : path;

          // make sure the path ends with /
          path = !path.endsWith('/') ? `${path}/` : path;

          // use absolute url for loading the offline handler
          config.offlineHandler = `${path}${config.baseUrlToken ? `${config.baseUrlToken}/` : ''}app-flow.js`;
        }

        // add PWA specific config
        if (PwaUtils.isWebPwaConfig(vbConfig)) {
          config.isWebPwa = true;
          config.registryUrl = vbConfig.REGISTRY_URL;
        }

        if (config.isWebPwa) {
          // enable processing of request with vb-use-cached-response-when-offline header for
          // extension registry url for web PWA
          config.useCacheResponseWhenOfflineHeaderEnabled = true;
        }

        config.modulePath = modulePath;

        config.performanceConfig = vbConfig.PERFORMANCE_CONFIG;

        if (externalConfig) {
          config.requireConfig = externalConfig.requireConfig;
          config.plugins = externalConfig.plugins;
          config.logConfig = externalConfig.logConfig;
          config.userConfig = externalConfig.userConfig;
          if (PwaUtils.isWebPwaConfig(vbConfig)) {
            config.registryConfig = externalConfig.registryConfig;
          }
        }

        config.traceOptions = Utils.getTraceOptions(vbConfig);

        return config;
      });
    }

    /**
     * Install the default service worker for the given scope. If service worker is not supported, it will be
     * emulated using the PersistenceManager from the offline toolkit running on the main thread.
     *
     * @param {String} scope the scope under which the service worker should be registered
     * @param config the config object for the service worker
     * @returns {Promise}
     * @protected
     */
    installServiceWorker(scope, config) {
      // remember the config
      this.config = config;

      return this.emulateServiceWorker(scope);
    }

    /**
     * Emulate the service worker functionality.
     *
     * @param scope scope for the emulated service worker. This is not the same
     * as the actual service worker scope. For example, for a service worker registered at
     * https://masterdev-vboci.integration.test.ocp.oc-test.com/ic/builder/rt/jpCRUD/1.0/webApps/web/sw.js
     * emulated service worker scope would be "/ic/builder/rt/jpCRUD/1.0/"
     * and for an application using vanity url, with service worker registered at https://myvbtest.myvb.org/sw.js
     * emulated service worker scope would be "/ic/builder/rt/jpMob2_1_0/live/"
     * Emulates service worker functionality when actual service workers are not supported.
     *
     * @param {string} scope - The scope for the emulated service worker.
     * @returns {Promise<EmulatedServiceWorkerWrapper>} Promise resolving to the emulated service worker wrapper.
     * @protected
    */
    emulateServiceWorker(scope) {
      this.emulateServiceWorkerPromise = this.emulateServiceWorkerPromise || Promise.resolve().then(() => {
        logger.info('Emulating service worker:');

        const browserFetch = globalThis.fetch;
        const browserXMLHttpRequest = globalThis.XMLHttpRequest;
        const alreadyEmulating = browserFetch && 'vbEmulatedServiceWorkerFetch' in browserFetch;
        // OPT installs PersistenceXMLHttpRequest, which already converts XHR into fetch. If it is present, leave it
        // in place so we do not add a second XHR adapter on top of OPT's browser API replacement.
        const alreadyEmulatingXMLHttpRequest = browserXMLHttpRequest
          && ('vbEmulatedServiceWorkerXMLHttpRequest' in browserXMLHttpRequest
            || String(browserXMLHttpRequest).indexOf('PersistenceXMLHttpRequest') >= 0);

        // remember the browser fetch and XHR
        this.config.browserFetch = browserFetch;
        this.config.browserXMLHttpRequest = browserXMLHttpRequest;

        this.serviceWorkerWrapper = new EmulatedServiceWorkerWrapper(scope, this.config);
        this.serviceWorkerWrapper.ownsFetchOverride = !alreadyEmulating;
        this.serviceWorkerWrapper.ownsXMLHttpRequestOverride = !!browserXMLHttpRequest
          && !alreadyEmulatingXMLHttpRequest;

        // install any message handlers that couldn't be installed before the service worker is available
        if (this.messageHandlerQueue) {
          this.messageHandlerQueue
            .forEach((messageHandler) => this.installMessageHandler(messageHandler));
          this.messageHandlerQueue = null;
        }

        // Only the first emulated wrapper owns global fetch interception. DT workers bootstrap their own
        // wrapper before runtime services initialize; later runtime wrappers should initialize normally but
        // leave requests flowing through the original DT wrapper, matching the old registration behavior.
        if (!alreadyEmulating) {
          const { serviceWorkerWrapper } = this;
          const emulatedFetch = (resource, options) => {
            const request = (resource instanceof Request && !options) ? resource : new Request(resource, options);

            return serviceWorkerWrapper.handleRequest(request);
          };
          Object.assign(emulatedFetch, { vbEmulatedServiceWorkerFetch: true });
          globalThis.fetch = emulatedFetch;
        }

        if (!alreadyEmulatingXMLHttpRequest && browserXMLHttpRequest) {
          // Convert legacy XHR traffic into fetch so requests still flow through the emulated service-worker
          // plugin chain. The adapter calls global fetch, preserving whichever fetch interceptor owns the page.
          const emulatedXMLHttpRequest = createEmulatedXMLHttpRequest(browserXMLHttpRequest);
          Object.assign(emulatedXMLHttpRequest, { vbEmulatedServiceWorkerXMLHttpRequest: true });
          this.serviceWorkerWrapper.emulatedXMLHttpRequest = emulatedXMLHttpRequest;
          globalThis.XMLHttpRequest = emulatedXMLHttpRequest;
        }

        // activate url mapper after the emulated service worker is created
        return this.activateUrlMapping().then(() => this.serviceWorkerWrapper);
      });

      return this.emulateServiceWorkerPromise;
    }

    /**
     * Caching is enabled unless vbInitConfig.SERVICE_WORKER_CONFIG.disabled is set to true.
     * In all other cases, it is enabled
    */
    isCachingEnabled() {
      if (!globalThis.vbInitConfig || !globalThis.vbInitConfig.SERVICE_WORKER_CONFIG) {
        return false;
      }
      return globalThis.vbInitConfig.SERVICE_WORKER_CONFIG.disabled === true;
    }

    /**
     * Extension Caching is not supported
     */
    isExtensionCachingEnabled() {
      return false;
    }

    /**
     * Tell the service worker to setup an extension's cache.
     * Out of date caches for this extension are deleted.
     * The route for the extension's cache is based on the extension's baseUrl.
     * @param {ExtensionCacheConfigGetter} getExtensionCacheConfig returns the extension cache configuration
     * @returns {Promise}
     */
    // eslint-disable-next-line no-unused-vars
    setupExtensionCache(getExtensionCacheConfig) {
      return Promise.resolve();
    }

    /**
     * Determine if the Service worker is in the process of caching extensions.
     * @returns {Promise<string>} 'IDLE' | 'CACHING' | 'WAITING'
     */
    getExtensionCachingStatus() {
      return Promise.resolve('IDLE');
    }
  }

  return ServiceWorkerManagerClass;
});

